import React, { useEffect, useState } from 'react'
import { appBootstrap } from '@/core/services/AppBootstrap'
import { ErrorDisplay } from '@/presentation/components/ui/ErrorDisplay'

interface AppInitializerProps {
  children: React.ReactNode
}

export const AppInitializer: React.FC<AppInitializerProps> = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)

  useEffect(() => {
    const initializeApp = async () => {
      try {
        await appBootstrap.initialize()
        setIsInitialized(true)
      } catch (err) {
        console.error('Failed to initialize application:', err)
        setError(
          err instanceof Error ? err : new Error('Unknown initialization error')
        )
      }
    }

    initializeApp()
  }, [])

  const handleRetry = async () => {
    setIsRetrying(true)
    setError(null)

    try {
      await appBootstrap.initialize()
      setIsInitialized(true)
    } catch (err) {
      console.error('Failed to retry initialization:', err)
      setError(
        err instanceof Error ? err : new Error('Unknown initialization error')
      )
    } finally {
      setIsRetrying(false)
    }
  }

  const getErrorType = (
    error: Error
  ): 'timeout' | 'network' | 'server' | 'unknown' => {
    const message = error.message.toLowerCase()

    if (
      message.includes('timeout') ||
      message.includes('504') ||
      message.includes('gateway')
    ) {
      return 'timeout'
    }

    if (
      message.includes('network') ||
      message.includes('connection') ||
      message.includes('fetch')
    ) {
      return 'network'
    }

    if (message.includes('500') || message.includes('internal server')) {
      return 'server'
    }

    return 'unknown'
  }

  if (error) {
    const errorType = getErrorType(error)

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <ErrorDisplay
          title="Erro ao inicializar a aplicação"
          errorType={errorType}
          onRetry={handleRetry}
          isRetrying={isRetrying}
        />
      </div>
    )
  }

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background ">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">
            Inicializando o modulo sócio Operador
          </h2>
          <p className="mt-2 text-gray-600">
            Configurando seu modulo de sócio...
          </p>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
